import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from '../http/http.service';
import { config } from '../../config';

export interface ClientData {
  clients: any[];
  totalCount: number;
  lastUpdated: Date;
  companyId?: string;
}

export interface PaginationParams {
  fromIndex: number;
  limit: number;
  orderBy: string;
  orderByOn: string;
  searchtext: string;
  company_id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientDataService {
  private clientDataSubject = new BehaviorSubject<ClientData | null>(null);
  public clientData$ = this.clientDataSubject.asObservable();

  private currentData: ClientData | null = null;

  constructor(private httpService: HttpService) {}

  /**
   * Check if trial period has expired based on created date and trial_limit_days
   */
  isTrialExpired(companyDetails: any): boolean {
    if (!companyDetails || !companyDetails.is_trial) {
      return false;
    }

    // If trial_limit_days is not set, assume unlimited trial
    if (!companyDetails.trial_limit_days) {
      return false;
    }

    // If created_at is not available, we can't validate
    if (!companyDetails.created_at) {
      return false;
    }

    const createdDate = new Date(companyDetails.created_at);
    const currentDate = new Date();
    const daysDifference = Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysDifference > companyDetails.trial_limit_days;
  }

  /**
   * Check if trial is active (not expired and is_trial is true)
   */
  isTrialActive(companyDetails: any): boolean {
    return companyDetails && 
           companyDetails.is_trial && 
           !this.isTrialExpired(companyDetails);
  }

  /**
   * Get remaining trial days
   */
  getRemainingTrialDays(companyDetails: any): number {
    if (!companyDetails || !companyDetails.is_trial || !companyDetails.trial_limit_days || !companyDetails.created_at) {
      return 0;
    }

    const createdDate = new Date(companyDetails.created_at);
    const currentDate = new Date();
    const daysPassed = Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    const remainingDays = companyDetails.trial_limit_days - daysPassed;

    return Math.max(0, remainingDays);
  }

  /**
   * Fetch clients data from API
   */
  fetchClients(params: PaginationParams): Observable<any> {
    return new Observable(observer => {
      this.httpService.postCall(`${config.apiUrl}/getclients`, params).subscribe(
        data => {
          // Update the shared data
          this.currentData = {
            clients: data.data || [],
            totalCount: data.count || 0,
            lastUpdated: new Date(),
            companyId: params.company_id
          };
          
          this.clientDataSubject.next(this.currentData);
          observer.next(data);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }

  /**
   * Get current client count without making API call
   */
  getCurrentClientCount(companyId?: string): number {
    if (!this.currentData) return 0;
    
    // If company ID is specified and doesn't match current data, return 0
    if (companyId && this.currentData.companyId !== companyId) {
      return 0;
    }
    
    return this.currentData.totalCount;
  }

  /**
   * Get current clients data without making API call
   */
  getCurrentClientsData(): ClientData | null {
    return this.currentData;
  }

  /**
   * Check if we have fresh data (less than 5 minutes old)
   */
  hasFreshData(companyId?: string): boolean {
    if (!this.currentData) return false;
    
    // Check if company ID matches
    if (companyId && this.currentData.companyId !== companyId) {
      return false;
    }
    
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return this.currentData.lastUpdated > fiveMinutesAgo;
  }

  /**
   * Force refresh data
   */
  refreshData(params: PaginationParams): Observable<any> {
    return this.fetchClients(params);
  }

  /**
   * Clear cached data
   */
  clearData(): void {
    this.currentData = null;
    this.clientDataSubject.next(null);
  }

  /**
   * Get client count for specific company (will fetch if needed)
   */
  getClientCount(companyId: string): Observable<number> {
    return new Observable(observer => {
      // If we have fresh data for this company, return it immediately
      if (this.hasFreshData(companyId)) {
        observer.next(this.getCurrentClientCount(companyId));
        observer.complete();
        return;
      }

      // Otherwise, fetch fresh data
      const params: PaginationParams = {
        fromIndex: 0,
        limit: 1, // We only need the count
        orderBy: 'DESC',
        orderByOn: 'id',
        searchtext: '',
        company_id: companyId
      };

      this.fetchClients(params).subscribe(
        data => {
          observer.next(data.count || 0);
          observer.complete();
        },
        error => {
          observer.error(error);
        }
      );
    });
  }
}